import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, RotateCcw, Loader2, CheckCircle2, AlertTriangle, Sparkles, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoCapture } from "@/components/PhotoCapture";
import { fmtTemp, parseTempInput } from "@/lib/format";
import { api, apiErr } from "@/lib/api";
import { queueCheck } from "@/lib/offline";

export default function RegistroTemperatura() {
  const loc = useLocation();
  const nav = useNavigate();
  const st = loc.state || {};
  const equipment = st.equipment;
  const [step, setStep] = useState("capture");
  const [photo, setPhoto] = useState(null);
  const [ocr, setOcr] = useState({ value: null, confidence: null });
  const [value, setValue] = useState("");
  const [manual, setManual] = useState(false);
  const [result, setResult] = useState(null);
  const [incidentId, setIncidentId] = useState(st.recheck_incident_id || null);
  const [caTypes, setCaTypes] = useState([]);
  const [caType, setCaType] = useState("");
  const [caNotes, setCaNotes] = useState("");
  const [caPhoto, setCaPhoto] = useState(null);
  const isRecheck = !!st.recheck_incident_id;

  useEffect(() => {
    if (!equipment) nav("/app");
  }, [equipment, nav]);

  if (!equipment) return null;
  const lo = equipment.min_temperature, hi = equipment.max_temperature, unit = equipment.unit || "C";

  const onCapture = async (dataUrl) => {
    setPhoto(dataUrl);
    if (!navigator.onLine) {
      setManual(true); setStep("confirm");
      toast.message("Sin conexión", { description: "Ingresa el valor manualmente. Se sincronizará luego." });
      return;
    }
    setStep("reading");
    try {
      const { data } = await api.post("/ocr/read", { photo_base64: dataUrl, mime_type: "image/jpeg" });
      setOcr({ value: data.ocr_value, confidence: data.ocr_confidence });
      if (data.ocr_value !== null && data.ocr_value !== undefined) {
        setValue(String(data.ocr_value).replace(".", ","));
        setManual(false);
      } else {
        setManual(true);
      }
    } catch {
      setManual(true);
    }
    setStep("confirm");
  };

  const save = async () => {
    const v = parseTempInput(value);
    if (v === null) { toast.error("Ingresa un valor de temperatura válido"); return; }
    const payload = {
      equipment_id: equipment.id, confirmed_value: v, unit,
      shift_id: st.shift_id || null, expected_control_id: st.expected_control_id || null,
      photo_base64: photo, ocr_value: ocr.value ?? null, ocr_confidence: ocr.confidence ?? null,
      entered_manually: manual, recheck_incident_id: st.recheck_incident_id || null,
    };
    if (!navigator.onLine) {
      await queueCheck(payload);
      setResult({ offline: true, in_range: lo <= v <= hi, value: v });
      setStep("result");
      toast.success("Guardado sin conexión", { description: "Pendiente de sincronización." });
      return;
    }
    setStep("saving");
    try {
      const { data } = await api.post("/temperature-checks", payload);
      setResult({ ...data, value: v });
      if (data.incident_id) setIncidentId(data.incident_id);
      setStep("result");
    } catch (e) {
      toast.error(apiErr(e));
      setStep("confirm");
    }
  };

  const loadCaTypes = async () => {
    try { const { data } = await api.get("/corrective-action-types"); setCaTypes(data); }
    catch { setCaTypes([]); }
  };

  const openCorrective = () => { loadCaTypes(); setStep("corrective"); };

  const submitCorrective = async () => {
    if (!caType) { toast.error("Selecciona una acción correctiva"); return; }
    try {
      await api.post(`/incidents/${incidentId}/corrective-action`, {
        corrective_action_type_id: caType, notes: caNotes, photo_base64: caPhoto,
      });
      toast.success("Acción correctiva registrada");
      setStep("recheck_prompt");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startRecheck = () => {
    nav("/app/registro", { replace: true, state: {
      equipment, shift_id: st.shift_id, recheck_incident_id: incidentId,
    }});
    // reset local state
    setStep("capture"); setPhoto(null); setOcr({ value: null, confidence: null });
    setValue(""); setResult(null); setManual(false);
  };

  const inRange = result ? (result.offline ? (lo <= result.value && result.value <= hi) : result.in_range) : null;

  return (
    <div className="mx-auto max-w-md px-4 pb-24 pt-5">
      <button data-testid="registro-back-btn" onClick={() => nav(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500">
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="font-display text-lg font-semibold">{equipment.name}</p>
        <p className="text-sm text-slate-500">{equipment.location_name} · {equipment.area_name}</p>
        <p className="mt-1 text-sm text-slate-600">Rango permitido: <span className="tnum font-medium">{fmtTemp(lo, unit)} a {fmtTemp(hi, unit)}</span></p>
        {isRecheck && <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700"><RotateCcw className="h-3.5 w-3.5" /> Segunda medición</p>}
      </div>

      {step === "capture" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <PhotoCapture onCapture={onCapture} testidPrefix="registro-photo"
            instruction="Toma una foto donde se vea claramente la temperatura del equipo."
            ctaLabel="Tomar foto de la temperatura" compact allowGallery />
          <p className="mt-3 text-center text-xs text-slate-400">La fotografía es obligatoria para cada registro.</p>
        </motion.div>
      )}

      {step === "reading" && (
        <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Leyendo temperatura…</p>
        </div>
      )}

      {step === "confirm" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {photo && <img src={photo} alt="captura" className="mx-auto max-h-48 w-auto rounded-2xl object-contain" />}
          {!manual && ocr.value !== null ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
              <p className="text-sm text-slate-500">Temperatura detectada</p>
              <p className="tnum font-display text-4xl font-semibold text-slate-900">{fmtTemp(ocr.value, unit)}</p>
              <p className="mt-1 text-xs text-slate-400">Confianza {Math.round((ocr.confidence || 0) * 100)}% · Confirma o corrige</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-700">
              No se pudo leer automáticamente. Ingresa el valor manualmente.
            </div>
          )}
          <div>
            <Label className="text-sm">Valor confirmado (°{unit})</Label>
            <Input data-testid="registro-value-input" value={value} inputMode="decimal"
              onChange={(e) => { setValue(e.target.value); setManual(true); }}
              className="mt-1 h-14 text-center tnum text-2xl" placeholder="0,0" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="registro-retake-btn" variant="outline" className="h-14 rounded-xl" onClick={() => { setStep("capture"); setPhoto(null); setValue(""); }}>
              <RotateCcw className="mr-2 h-5 w-5" /> Repetir foto
            </Button>
            <Button data-testid="registro-save-btn" className="h-14 rounded-xl text-base" onClick={save}>
              <Check className="mr-2 h-5 w-5" /> Confirmar
            </Button>
          </div>
        </motion.div>
      )}

      {step === "saving" && (
        <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-primary" /> Guardando registro…
        </div>
      )}

      {step === "result" && (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="space-y-5 text-center">
          <div className={`rounded-2xl border p-6 ${inRange ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
            {inRange ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /> : <AlertTriangle className="mx-auto h-12 w-12 text-red-600" />}
            <p className="mt-3 font-display text-xl font-semibold" data-testid="registro-result-title">
              {inRange ? "Registro completado" : "Temperatura fuera de rango"}
            </p>
            <p className="tnum mt-1 text-3xl font-semibold">{fmtTemp(result.value, unit)}</p>
            <p className="mt-1 text-sm text-slate-600">{inRange ? "Dentro de rango" : `Fuera del rango ${fmtTemp(lo, unit)}–${fmtTemp(hi, unit)}`}</p>
            {result.offline && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600"><CloudOff className="h-3.5 w-3.5" /> Pendiente de sincronización</p>}
            {isRecheck && result.incident_status === "RESOLVED" && <p className="mt-3 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">Incidencia resuelta</p>}
          </div>

          {!inRange && !result.offline && (
            <Button data-testid="registro-corrective-btn" className="h-14 w-full rounded-xl text-base" onClick={openCorrective}>
              Registrar acción correctiva
            </Button>
          )}
          <Button data-testid="registro-done-btn" variant={inRange ? "default" : "outline"} className="h-12 w-full rounded-xl" onClick={() => nav("/app")}>
            Finalizar
          </Button>
        </motion.div>
      )}

      {step === "corrective" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <p className="font-display text-lg font-semibold">Acción correctiva</p>
          <div>
            <Label className="text-sm">¿Qué acción tomaste?</Label>
            <Select value={caType} onValueChange={setCaType}>
              <SelectTrigger data-testid="corrective-type-select" className="mt-1 h-12"><SelectValue placeholder="Selecciona una acción" /></SelectTrigger>
              <SelectContent>
                {caTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Notas (opcional)</Label>
            <Textarea data-testid="corrective-notes-input" value={caNotes} onChange={(e) => setCaNotes(e.target.value)} className="mt-1" placeholder="Detalle de la acción" />
          </div>
          <div>
            <Label className="text-sm">Fotografía de evidencia (opcional)</Label>
            {caPhoto ? (
              <div className="mt-2">
                <img src={caPhoto} alt="evidencia" className="aspect-[4/3] w-full rounded-xl object-cover" />
                <Button data-testid="corrective-photo-clear" variant="ghost" size="sm" className="mt-1 text-slate-500" onClick={() => setCaPhoto(null)}>Quitar foto</Button>
              </div>
            ) : (
              <div className="mt-2"><PhotoCapture onCapture={setCaPhoto} testidPrefix="corrective-photo" instruction="Foto de la evidencia de la acción correctiva." ctaLabel="Tomar foto" compact allowGallery /></div>
            )}
          </div>
          <Button data-testid="corrective-submit-btn" className="h-14 w-full rounded-xl text-base" onClick={submitCorrective}>
            Guardar acción
          </Button>
        </motion.div>
      )}

      {step === "recheck_prompt" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 text-center">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <RotateCcw className="mx-auto h-12 w-12 text-amber-600" />
            <p className="mt-3 font-display text-xl font-semibold">Nueva medición requerida</p>
            <p className="mt-1 text-sm text-slate-600">Realiza una segunda medición para verificar si la temperatura volvió al rango.</p>
          </div>
          <Button data-testid="registro-recheck-btn" className="h-14 w-full rounded-xl text-base" onClick={startRecheck}>
            Hacer segunda medición
          </Button>
          <Button variant="outline" className="h-12 w-full rounded-xl" onClick={() => nav("/app")}>Más tarde</Button>
        </motion.div>
      )}
    </div>
  );
}
